import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react'
import { use, useLayoutEffect, useRef, useState } from 'react'
import useEvent from 'react-use-event-hook'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'

import { cn } from '@gto/ui/cn'

import { tabsIndicatorVariants, tabsListVariants } from './tabs.styles'
import { TabsContext } from './tabs-context'

/**
 * Accessible-name attributes for the tablist. Any headings rendered inside
 * the list are presentational chrome, so a tablist without one of these has
 * no name at all — give every `List` an `aria-label`, or point
 * `aria-labelledby` at a visible heading.
 */
type ListAriaProps = Pick<
  ComponentPropsWithoutRef<typeof BaseTabs.List>,
  'aria-label' | 'aria-labelledby'
>

export type ListProps = ListAriaProps & {
  className?: string
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
}

/** How far one chevron click scrolls the list, in px */
const SCROLL_STEP_PX = 160

/**
 * Chevron used by the scrollable arrow buttons — inlined directly from the
 * Figma scrollable-tabs export so the glyph matches Figma exactly: filled
 * path with rounded caps, ~11.5×7 in a 24×24 viewBox, ~2.17px stroke.
 * Uses `currentColor` so it inherits the button's text colour
 * (action-active / hover white / disabled 18%). CSS rotation handles the
 * four directions: down (original), up (180°), left (90° CW), right (90° CCW).
 */
const CHEVRON_PATH =
  'M6.2636 9.86358C6.61508 9.51211 7.18492 9.51211 7.5364 9.86358L12 14.3272L16.4636 9.86358C16.8151 9.51211 17.3849 9.51211 17.7364 9.86358C18.0879 10.2151 18.0879 10.7849 17.7364 11.1364L12.6364 16.2364C12.4676 16.4052 12.2387 16.5 12 16.5C11.7613 16.5 11.5324 16.4052 11.3636 16.2364L6.2636 11.1364C5.91213 10.7849 5.91213 10.2151 6.2636 9.86358Z'

type ChevronDirection = 'up' | 'down' | 'left' | 'right'

const TabsScrollChevron = ({ direction }: { direction: ChevronDirection }) => (
  <svg
    aria-hidden="true"
    fill="none"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={cn(
      direction === 'up' && 'rotate-180',
      direction === 'left' && 'rotate-90',
      direction === 'right' && '-rotate-90',
    )}
  >
    <path
      clipRule="evenodd"
      d={CHEVRON_PATH}
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
)

// Scroll-arrow button — Figma scrollable-tabs node (13606-225618). Bespoke
// rather than `@gto/ui/icon-button` because IconButton's contract is fixed at
// 24/32/48 px sizes with `rounded-full`, while Figma's tabs scroll-arrow is
// 38×38 with `rounded-[8px]`. The shape + the four state colours are
// tabs-specific, so the class string lives here rather than widening
// IconButton's surface for a single component.
//
// Keyboard tab order: the chevrons carry `tabIndex={-1}` (see the button
// renderers below) so they sit outside the page's tab order. WAI-ARIA tabs
// delegate keyboard scrolling to the arrow keys on the tabs themselves (the
// focused tab is scrolled into view by the browser); the chevrons are a
// mouse / touch affordance only.
//
// 38×38 hit area (= tab height), 8px radius, transparent at rest. The icon
// is the inlined Figma chevron rendered at 24×24 (Figma's icon-frame size;
// the glyph itself is ~11.5×7 within that). Chevron colour: action-active at
// rest (the brighter 56% grey), full white on hover/focus/press, and
// white@18% when DISABLED at a scroll boundary.
const ARROW_BUTTON_CLASS = [
  'absolute z-10 flex items-center justify-center',
  'size-[38px] rounded-[8px]',
  'bg-transparent text-text-action-active',
  'cursor-pointer border-none',
  'transition-[background-color,color] duration-150',
  'hover:bg-states-hover hover:text-text-primary',
  'focus-visible:outline-none focus-visible:bg-states-focus focus-visible:text-text-primary',
  'active:bg-states-selected active:text-text-primary',
  'disabled:cursor-default disabled:bg-transparent disabled:text-white/[0.18]',
  '[&_svg]:size-[24px]',
].join(' ')

/**
 * Horizontal or vertical row of `Tab` triggers. Orientation, color,
 * `fullWidth` and `scrollable` are inherited from the parent `Root`.
 *
 * When `scrollable` is set on `Root`, the list becomes a scroll container
 * with chevron buttons at the leading and trailing edges. The buttons
 * disable themselves when the matching edge is reached so the user sees
 * a true affordance, not a perpetually-rendered arrow. With
 * `scrollable="auto"` the chevrons render only while the content overflows.
 */
export const List = ({
  className,
  children,
  ref,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: ListProps) => {
  const { color, fullWidth, scrollable, detached, indicator, orientation, paperLevel } =
    use(TabsContext)
  const listRef = useRef<HTMLDivElement | null>(null)
  // Defaults cover the first render only — the layout effect below corrects
  // both boundaries against real geometry before paint.
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const isVertical = orientation === 'vertical'
  const isScrollLayout = scrollable !== false

  const updateScrollBoundaries = useEvent(function detectScrollBoundaries() {
    const el = listRef.current
    if (!isScrollLayout || el == null) {
      return
    }
    if (isVertical) {
      setAtStart(el.scrollTop <= 0)
      setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 1)
    } else {
      setAtStart(el.scrollLeft <= 0)
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
    }
  })

  // A layout effect so the FIRST boundary check lands before paint — in
  // `'auto'` mode a fitting strip would otherwise flash its chevrons for one
  // frame while `atEnd` still holds its render default.
  useLayoutEffect(
    function observeListSize() {
      if (!isScrollLayout) {
        return undefined
      }
      const el = listRef.current
      if (el == null) {
        return undefined
      }
      updateScrollBoundaries()
      el.addEventListener('scroll', updateScrollBoundaries, { passive: true })

      // ResizeObserver may be missing in non-browser environments (jsdom).
      // Skip the observer there — boundary detection still works on scroll
      // events, just without size-change reactivity.
      if (typeof ResizeObserver === 'undefined') {
        return () => {
          el.removeEventListener('scroll', updateScrollBoundaries)
        }
      }
      const observer = new ResizeObserver(updateScrollBoundaries)
      observer.observe(el)

      return () => {
        observer.disconnect()
        el.removeEventListener('scroll', updateScrollBoundaries)
      }
    },
    [isScrollLayout, updateScrollBoundaries],
  )

  // Re-measure on EVERY commit, not just on resize: adding or removing a tab —
  // or a label growing under a locale switch — changes `scrollWidth` without
  // resizing the list's own box, which the ResizeObserver above cannot see.
  // Every such change flows through a render of this component, so this closes
  // that gap with two scroll-metric reads and no extra observers.
  useLayoutEffect(updateScrollBoundaries)

  const scrollBy = useEvent(function scrollByStep(direction: -1 | 1) {
    const el = listRef.current
    if (el == null) {
      return
    }
    if (isVertical) {
      el.scrollBy({ top: direction * SCROLL_STEP_PX, behavior: 'smooth' })
    } else {
      el.scrollBy({ left: direction * SCROLL_STEP_PX, behavior: 'smooth' })
    }
  })

  const indicatorElement = (
    <BaseTabs.Indicator
      className={cn(tabsIndicatorVariants({ orientation, paperLevel, detached, indicator, color }))}
    />
  )

  const setListRefs = (node: HTMLDivElement | null) => {
    listRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref != null) {
      ref.current = node
    }
  }

  if (!isScrollLayout) {
    return (
      <BaseTabs.List
        ref={setListRefs}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(tabsListVariants({ fullWidth, detached }), className)}
      >
        {children}
        {indicatorElement}
      </BaseTabs.List>
    )
  }

  // `'auto'` always renders this scroll layout; while the content fits
  // (`atStart && atEnd` — the same signal that disables the chevrons at the
  // boundaries) the chevrons and their padding reservation drop away. The
  // strip stays a real scroll container either way, so overflowing tabs
  // remain reachable even if a measurement goes stale.
  const hideArrows = scrollable === 'auto' && atStart && atEnd
  const startChevronDirection: ChevronDirection = isVertical ? 'up' : 'left'
  const endChevronDirection: ChevronDirection = isVertical ? 'down' : 'right'

  return (
    // `className` on `<List>` always lands on `BaseTabs.List` (below), not on
    // this scroll-wrapper — so width/spacing classes from a consumer like
    // `<List className="w-[128px]">` style the actual tab list in both
    // scrollable and non-scrollable modes. The wrapper holds only the layout
    // primitives needed to position the absolute arrow buttons.
    <div
      className={cn(
        'relative',
        isVertical ? 'flex flex-col' : 'flex flex-row',
        // Padding to make room for the absolute arrows so they don't sit on top of tabs
        !hideArrows && (isVertical ? 'py-12' : 'px-12'),
      )}
    >
      {!hideArrows && (
        <button
          aria-label={isVertical ? 'Scroll up' : 'Scroll left'}
          disabled={atStart}
          tabIndex={-1}
          type="button"
          className={cn(
            ARROW_BUTTON_CLASS,
            isVertical ? 'left-1/2 top-0 -translate-x-1/2' : 'left-0 top-1/2 -translate-y-1/2',
          )}
          onClick={() => scrollBy(-1)}
        >
          <TabsScrollChevron direction={startChevronDirection} />
        </button>
      )}
      <BaseTabs.List
        ref={setListRefs}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          tabsListVariants({ fullWidth, detached }),
          isVertical
            ? 'h-full max-h-full overflow-y-auto overflow-x-hidden'
            : 'w-full max-w-full overflow-x-auto overflow-y-hidden',
          // Hide native scrollbar — keeps the design clean; arrows are the affordance
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
        )}
      >
        {children}
        {indicatorElement}
      </BaseTabs.List>
      {!hideArrows && (
        <button
          aria-label={isVertical ? 'Scroll down' : 'Scroll right'}
          disabled={atEnd}
          tabIndex={-1}
          type="button"
          className={cn(
            ARROW_BUTTON_CLASS,
            isVertical ? 'bottom-0 left-1/2 -translate-x-1/2' : 'right-0 top-1/2 -translate-y-1/2',
          )}
          onClick={() => scrollBy(1)}
        >
          <TabsScrollChevron direction={endChevronDirection} />
        </button>
      )}
    </div>
  )
}
